import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import _ from 'lodash';
import { TournamentService } from '../../../services/tournament.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LogService } from '../../../services/log.service';
import { Tournament } from '../../../models/tournament';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { StandingGroup } from '../../../models/standingGroup';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs';
import { StandingService } from '../../../services/standing.service';
import { Popover, PopoverModule } from 'primeng/popover';
import { Standing } from '../../../models/standing';
import { AuxiliarService } from '../../../services/auxiliar.service';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../services/auth.service';
import { RoundService } from '../../../services/round.service';

@Component({
  selector: 'app-tournament-detail-standing',
  imports: [
    ButtonModule,
    CommonModule,
    Popover,
    PopoverModule,
    TableModule,
    TooltipModule,
    TranslateModule,
  ],
  templateUrl: './tournament-detail-standing.component.html',
  styleUrl: './tournament-detail-standing.component.scss'
})
export class TournamentDetailStandingComponent implements OnInit {
  @ViewChild('moveTeamPop', { static: true }) moveTeamPop!: Popover;

  public _ = _;
  public allowChangePositions: boolean = false;
  private readonly destroy$ = inject(DestroyRef);
  public filter = computed(() => this.auxiliarService.searchField());
  public loading: boolean = true;
  public selectedView: string = 'detail';
  public standingsGroups: StandingGroup[] = [];
  public standingToMove: Standing | undefined;
  public standingToMoveGroup: StandingGroup | undefined;
  public standingToMovePositions: number[] = [];
  public tournament: Tournament | undefined;

  constructor(
    public authService: AuthService,
    private auxiliarService: AuxiliarService,
    private cdr: ChangeDetectorRef,
    private logService: LogService,
    private roundService: RoundService,
    private standingService: StandingService,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.tournament = this.tournamentService.tournament();
    this.checkAllMatchesFinished();
    this.getStandings();
  }

  private checkAllMatchesFinished(): void {
    if(!this.tournament?.id) return;
    this.roundService.checkRoundsFinished(this.tournament.id).pipe(
      takeUntilDestroyed(this.destroy$),
    )
    .subscribe({
      next: (res) => {
        this.logService.debug('[Standings] - checkRoundsFinished', res);
        this.allowChangePositions = 
          !_.get(res, 'body.data.playoffsStarted', false) &&
          this.authService.hasRole(['admin', 'staff']);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.logService.error('[Standings] - checkRoundsFinished', err);
      }
    });
  }

  private getStandings(): void {
    if(!this.tournament?.id) return;
    this.standingService.getTournamentStandings(this.tournament.id)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        finalize(() => this.loading = false),
      )
      .subscribe({
        next: (res) => {
          this.logService.debug('[Standings] - getTournamentStandings', res);
          this.standingsGroups = _.orderBy(_.map(_.get(res, 'body.data'), (group: StandingGroup) => {
            return {
              ...group,
              groupName: `${this.translate.instant('field.group')} ${group.groupName}`,
              standings: _.orderBy(group.standings, 'groupPosition')
            };
          }), 'groupName');
          setTimeout(() => {
            this.cdr.markForCheck();
          });
        },
        error: (err) => {
          this.logService.error('[Standings] - getTournamentStandings', err, this.translate.instant('error.standings'));
        }
      });
  }

  public toggleMoveTeamPop(event: Event, standing: Standing, group: StandingGroup): void {
    this.standingToMove = standing;
    this.standingToMoveGroup = group;
    this.standingToMovePositions = Array.from(
      { length: group?.standings?.length ?? 0 },
      (_, i) => i + 1
    );
    // open pop up
    this.moveTeamPop.toggle(event);
  }

  public moveStanding(position: number): void {
    const positions = _.map(this.standingToMoveGroup?.standings, (standing: Standing) => {
      const moveType = position < this.standingToMove?.groupPosition! ? 'up' : 'down';
      // standing to move
      if(standing.groupPosition === this.standingToMove?.groupPosition) {
        return {id: standing.teamId, groupPosition: position};
      }
      // up move 
      else if(moveType === 'up') {
        if(standing.groupPosition! < position || standing.groupPosition! > this.standingToMove?.groupPosition!) {
          return {id: standing.teamId, groupPosition: standing.groupPosition};
        } else {
          return {id: standing.teamId, groupPosition: standing.groupPosition! + 1};
        }
      }
      // down move
      else {
        if(standing.groupPosition! > position || standing.groupPosition! < this.standingToMove?.groupPosition!) {
          return {id: standing.teamId, groupPosition: standing.groupPosition};
        } else {
          return {id: standing.teamId, groupPosition: standing.groupPosition! - 1};
        }
      }
    });

    this.standingService.updateStandingsPositions(positions)
      .pipe(
        takeUntilDestroyed(this.destroy$),
        finalize(() => this.moveTeamPop.hide()),
      )
      .subscribe({
        next: (res) => {
          this.logService.debug('[Standings] - updateStandingsPositions', res);
          this.getStandings();
        },
        error: (err) => {
          this.logService.error('[Standings] - updateStandingsPositions', err, this.translate.instant('error.standings-update'));
        }
      });
  }
}
